import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowRight, FiBell, FiCheck, FiEdit2, FiPlus, FiRotateCcw, FiSettings, FiTrash2, FiTruck, FiX } from "react-icons/fi";
import { useUserId } from "../UserIdContext";
import { createApiClient, getAccessToken } from "../lib/apiClient";
import { activateAdminPush, deviceSettings, playNotificationSound, renewAdminPush, showAdminDesktopNotification, stopBrowserNotifications, supportsAdminPush, updatePushSound } from "../lib/adminPushClient";
import "./AdminNotificationHub.css";
const api = createApiClient();
const dateOptions = { timeZone: "America/Argentina/Buenos_Aires" };
const dayKey = (date) => new Date(date).toLocaleDateString("en-CA", dateOptions);
const noticeTime = (date) => new Date(date).toLocaleTimeString("es-AR", { ...dateOptions, hour: "2-digit", minute: "2-digit", hour12: false });
function noticeGroup(date) {
  const today = new Date();
  if (dayKey(date) === dayKey(today)) return "Hoy";
  if (dayKey(date) === dayKey(new Date(today.getTime() - 86400000))) return "Ayer";
  return new Date(date).toLocaleDateString("es-AR", { ...dateOptions, day: "numeric", month: "long", year: "numeric" });
}
function noticeDetails(item) {
  const [header = "", ...lines] = (item.body || "").split("\n");
  const parts = header.split(" · ");
  const context = parts.find((part) => /^(Binder [12]|Gestión general)$/.test(part)) || "";
  const detail = lines.join("\n");
  const origin = detail.match(/^Origen (fiscal|nacional):\s*/i);
  return {
    binder: item.deposito === "binder1" ? "Binder 1" : item.deposito === "binder2" ? "Binder 2" : context,
    origin: origin ? `Origen ${origin[1].toLowerCase()}` : "",
    actor: item.actorName || (parts.length > 1 ? parts[0] : ""),
    role: item.actorRole || (parts.length > 2 ? parts[1] : ""),
    detail: origin ? detail.slice(origin[0].length) : detail || (!context ? header : ""),
  };
}
function NoticeIcon({ title }) {
  const Icon = /reabierta/i.test(title) ? FiRotateCcw : /completada|finalizad/i.test(title) ? FiCheck
    : /eliminad|cancelad/i.test(title) ? FiTrash2 : /cread/i.test(title) ? FiPlus
      : /camión|operación|trasladado/i.test(title) ? FiTruck : FiEdit2;
  return <Icon aria-hidden="true" />;
}
const openLabel = (title) => /anotación/i.test(title) ? "Abrir anotación" : /actividad|programación/i.test(title) ? "Abrir actividad"
  : /camión|operación|SIEL|facturación/i.test(title) ? "Abrir operación" : "Abrir registro";

export default function AdminNotificationHub() {
  const { user, isAdmin } = useUserId();
  const location = useLocation(), navigate = useNavigate();
  const active = isAdmin && !!getAccessToken() && location.pathname !== "/";
  const userId = String(user?.id || user?._id || "");
  const session = useRef({ key: "", generation: 0 });
  const sessionKey = active ? userId : "";
  if (session.current.key !== sessionKey) {
    session.current = { key: sessionKey, generation: session.current.generation + 1 };
  }
  const [open, setOpen] = useState(false), [authorized, setAuthorized] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false), [filter, setFilter] = useState("all"), [selectedId, setSelectedId] = useState(null);
  const [list, setList] = useState({ items: [], unread: 0 });
  const [device, setDevice] = useState({ enabled: false, sound: true });
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [initializing, setInitializing] = useState(true);
  const acting = useRef(false);
  const alive = useRef(false), deviceRef = useRef(device), seen = useRef(new Set());
  const historySeen = useRef(null);
  const bellRef = useRef(null), closeRef = useRef(null);
  deviceRef.current = device;
  const load = useCallback(async () => {
    const generation = session.current.generation;
    if (session.current.key !== userId || !userId) return;
    try {
      const { data } = await api.get("/api/notificaciones-admin");
      if (alive.current && session.current.generation === generation) {
        const previous = historySeen.current;
        historySeen.current = new Set([...(previous || []), ...data.items.map((item) => item.id)]);
        setList(data); setAuthorized(true);
        if (previous && deviceRef.current.enabled) {
          data.items.filter((item) => !previous.has(item.id)).reverse().forEach((item) => {
            showAdminDesktopNotification(item, userId, deviceRef.current.sound).catch((err) => {
              historySeen.current?.delete(item.id);
              if (alive.current) setError(err.message);
            });
          });
        }
      }
    } catch (err) {
      if (!alive.current || session.current.generation !== generation) return;
      if (err?.response?.status === 403) { setAuthorized(false); stopBrowserNotifications(); }
      else setError("No se pudieron cargar los avisos. Podés reintentar al abrir este panel.");
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    historySeen.current = null; seen.current.clear();
    alive.current = active;
    if (!active) {
      setOpen(false); setSettingsOpen(false); setSelectedId(null); setList({ items: [], unread: 0 });
      deviceSettings().then((settings) => !cancelled && settings?.enabled && stopBrowserNotifications()).catch(() => {});
      return () => { cancelled = true; };
    }
    setList({ items: [], unread: 0 }); setOpen(false); setSelectedId(null);
    setAuthorized(true); setInitializing(true); load();
    deviceSettings().then(async (settings) => {
      if (cancelled) return;
      if (settings?.enabled && settings.userId === userId) {
        try { await activateAdminPush(userId, settings.sound, false); if (!cancelled) setDevice(settings); }
        catch (err) { if (!cancelled) setError(err.message); }
      } else { await stopBrowserNotifications(); if (!cancelled) setDevice({ enabled: false, sound: true }); }
    }).catch(() => {}).finally(() => { if (!cancelled) setInitializing(false); });
    const timer = setInterval(load, 5000);
    const renewal = setInterval(() => {
      if (deviceRef.current.enabled) renewAdminPush(userId, deviceRef.current.sound).catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message);
      });
    }, 60000);
    const message = (event) => {
      const payload = event.data;
      if (payload?.type !== "BINDER_ADMIN_NOTIFICATION" || payload.userId !== userId || seen.current.has(payload.id)) return;
      seen.current.add(payload.id); load();
      // Una sola pestaña reproduce el sonido para evitar avisos duplicados.
      const play = async () => {
        if (!deviceRef.current.enabled || !deviceRef.current.sound || !payload.sound || payload.playSound === false
          || (payload.soundBefore && Date.now() > payload.soundBefore)) return false;
        const key = `binder-notification-sound:${userId}:${payload.id}`;
        if (localStorage.getItem(key)) return true;
        const played = await playNotificationSound();
        if (played) localStorage.setItem(key, "played");
        return played;
      };
      const attempt = navigator.locks ? navigator.locks.request("binder-notification-sound", play) : play();
      Promise.resolve(attempt).then((played) => event.ports?.[0]?.postMessage({ played: !!played }))
        .catch(() => event.ports?.[0]?.postMessage({ played: false }));
    };
    navigator.serviceWorker?.addEventListener("message", message);
    return () => { cancelled = true; alive.current = false; clearInterval(timer); clearInterval(renewal); navigator.serviceWorker?.removeEventListener("message", message); };
  }, [active, userId, load]);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  const closePanel = () => { setOpen(false); bellRef.current?.focus(); };

  async function action(callback) {
    if (acting.current) return;
    acting.current = true;
    setBusy(true); setError("");
    try { await callback(); }
    catch (err) { setError(err?.response?.data?.error || err.message || "No se pudo completar el cambio."); }
    finally { acting.current = false; setBusy(false); }
  }
  const enable = () => action(async () => {
    if (device.sound) playNotificationSound();
    await activateAdminPush(userId, device.sound);
    setDevice({ enabled: true, sound: device.sound });
  });
  async function markRead(item) {
    await action(async () => {
      await api.post("/api/notificaciones-admin/leer", item ? { id: item.id } : {}); await load();
    });
  }
  function select(item) {
    setSelectedId(item.id);
    if (!item.read) markRead(item);
  }
  const visibleItems = list.items.filter((item) => filter === "all" || !item.read);
  const selected = list.items.find((item) => item.id === selectedId);
  const details = selected ? noticeDetails(selected) : null;
  const groups = visibleItems.reduce((result, item) => {
    const label = noticeGroup(item.createdAt);
    if (result.at(-1)?.label !== label) result.push({ label, items: [] });
    result.at(-1).items.push(item);
    return result;
  }, []);
  if (!active || !authorized || location.pathname === "/") return null;
  return createPortal(<div className="admin-notification-hub">
    <button ref={bellRef} type="button" className="admin-notification-bell" aria-label={`Notificaciones del administrador${list.unread ? `: ${list.unread} sin leer` : ""}`}
      aria-expanded={open} aria-controls="admin-notification-panel" onClick={() => {
        if (open) closePanel(); else { setOpen(true); setSettingsOpen(false); setError(""); load(); }
      }}>
      <FiBell aria-hidden="true" />{list.unread > 0 && <span>{list.unread > 99 ? "99+" : list.unread}</span>}
    </button>
    {open && <section id="admin-notification-panel" className="admin-notification-panel" role="dialog" aria-label="Notificaciones del administrador"
      onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); if (settingsOpen) setSettingsOpen(false); else closePanel(); } }}>
      <header><h2>Notificaciones</h2><div className="admin-notification-header-actions">
        <button type="button" aria-label="Configurar notificaciones" aria-expanded={settingsOpen} aria-controls="admin-notification-settings"
          onClick={() => setSettingsOpen(!settingsOpen)}><FiSettings aria-hidden="true" /></button>
        <button ref={closeRef} type="button" aria-label="Cerrar notificaciones" onClick={closePanel}><FiX aria-hidden="true" /></button>
      </div></header>
      {error && <p role="alert" className="admin-notification-error">{error}</p>}
      {settingsOpen ? <div id="admin-notification-settings" className="admin-notification-settings">
        <h3>Avisos de escritorio</h3>
        <p>Recibí los cambios importantes de otros usuarios, incluso con la pestaña cerrada.</p>
        {!supportsAdminPush() && <p>Este navegador necesita HTTPS y soporte de notificaciones push para los avisos de escritorio.</p>}
        <button type="button" disabled={busy || initializing || !supportsAdminPush()} onClick={device.enabled
          ? () => action(async () => { await stopBrowserNotifications(); setDevice({ ...device, enabled: false }); }) : enable}>
          {busy || initializing ? "Preparando…" : device.enabled ? "Desactivar avisos de escritorio" : "Activar avisos de escritorio"}</button>
        <label><input type="checkbox" checked={device.sound} disabled={busy || initializing} onChange={(event) => {
          const sound = event.target.checked;
          action(async () => { if (device.enabled) await updatePushSound(userId, sound); setDevice({ ...device, sound }); if (sound) playNotificationSound(); });
        }} /> Sonido de notificaciones</label>
        <small>Con la pestaña cerrada, el sonido depende de los ajustes del navegador y Windows.</small>
        <button type="button" onClick={() => setSettingsOpen(false)}>Volver a las notificaciones</button>
      </div> : <>
        <div className="admin-notification-toolbar">
          <div className="admin-notification-filters" role="group" aria-label="Filtrar notificaciones">
            <button type="button" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>Todas</button>
            <button type="button" aria-pressed={filter === "unread"} onClick={() => setFilter("unread")}>Sin leer</button>
          </div>
          <button type="button" className="admin-notification-read-all" disabled={busy || !list.unread} onClick={() => markRead()}>Marcar todas como leídas</button>
        </div>
        <div className="admin-notification-workspace">
          <div className="admin-notification-history" aria-label="Lista de notificaciones">
            {!visibleItems.length ? <p className="admin-notification-empty">{filter === "unread" ? "No tenés notificaciones sin leer." : "Todavía no hay cambios para mostrar."}</p>
              : groups.map((group) => <section key={group.label} aria-label={group.label}>
                <h3 className="admin-notification-day">{group.label}</h3>
                {group.items.map((item) => {
                  const info = noticeDetails(item);
                  return <button type="button" key={item.id} aria-pressed={selectedId === item.id}
                    className={`admin-notification-item${item.read ? " read" : ""}${selectedId === item.id ? " selected" : ""}`} onClick={() => select(item)} disabled={busy}>
                    <span className="admin-notification-item-icon"><NoticeIcon title={item.title} /></span>
                    <span className="admin-notification-item-copy"><strong>{item.title}</strong>
                      <span>{[info.binder, info.origin].filter(Boolean).join(" · ") || "Cambio en el sistema"}</span></span>
                    {!item.read && <span className="admin-notification-unread" aria-label="Sin leer" />}
                    <time dateTime={item.createdAt}>{noticeTime(item.createdAt)}</time>
                  </button>;
                })}
              </section>)}
          </div>
          <section className="admin-notification-detail" aria-label="Detalle de la notificación">
            {selected ? <>
              <h3><NoticeIcon title={selected.title} />{selected.title}</h3>
              <dl>
                {details.binder && <><dt>{details.binder === "Gestión general" ? "Ámbito" : "Binder"}</dt><dd>{details.binder}</dd></>}
                {details.origin && <><dt>Origen</dt><dd>{details.origin}</dd></>}
                {details.role && <><dt>Perfil</dt><dd>{details.role}</dd></>}
                {details.actor && <><dt>Usuario</dt><dd>{details.actor}</dd></>}
                <dt>Fecha</dt><dd><time dateTime={selected.createdAt}>{new Date(selected.createdAt).toLocaleString("es-AR", { ...dateOptions, day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}</time></dd>
              </dl>
              {details.detail && <p className="admin-notification-description">{details.detail}</p>}
              {selected.url && <div className="admin-notification-detail-footer"><button type="button" onClick={() => { closePanel(); navigate(selected.url); }}>
                {openLabel(selected.title)}<FiArrowRight aria-hidden="true" /></button></div>}
            </> : <div className="admin-notification-detail-placeholder"><FiBell aria-hidden="true" /><p>Seleccioná una notificación para ver el detalle.</p></div>}
          </section>
        </div>
      </>}
    </section>}
  </div>, document.body);
}
